import { AlertCircle, Check, Loader2 } from 'lucide-react';
import type { SoteriaBuildStep } from '../hooks/useSoteriaChat';
import { cn } from '@/lib/utils';

/**
 * BuildProgressChecklist
 * ----------------------
 * What a strategy build is doing, and how far through it is.
 *
 * WHY. Building a strategy takes five to eight minutes, and for all of it the
 * advisor used to get one line of rotating text. That line could not say how
 * much was left, and — because four of the five steps run concurrently and all
 * four wrote the same single slot — it frequently was not even describing the
 * work in progress. A wait that long with no sense of progress reads as a hang.
 *
 * The backend now publishes the whole plan before the first step starts, so
 * this can be a determinate checklist: every step visible from the first poll,
 * ticked as each finishes, with a count and the elapsed time. Nothing here
 * animates on its own beyond the single running spinner — a wait is calmer when
 * the only thing moving is the thing that is actually happening.
 */
export function BuildProgressChecklist({
  steps,
  elapsedSeconds,
}: {
  steps: Array<SoteriaBuildStep>;
  elapsedSeconds?: number;
}) {
  if (!steps.length) return null;

  // "done" is the count that has actually finished. A failed step is finished
  // too, but counting it as progress would let a broken build read as an
  // advancing one.
  const done = steps.filter((s) => s.status === 'done').length;
  const failed = steps.some((s) => s.status === 'failed');
  // The step being worked on, for the header line. With a concurrent wave there
  // can be several at once, so the count is what carries the real position —
  // this is a "which of them is furthest along" hint, not a claim of exclusivity.
  const position = Math.min(done + 1, steps.length);

  return (
    <div
      className="mb-3.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5"
      role="status"
      aria-live="polite"
      aria-label={`Building your strategy, step ${position} of ${steps.length}`}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-foreground">
          {failed
            ? 'Building your strategy: stopped'
            : `Building your strategy: ${done} of ${steps.length} done`}
        </span>
        {elapsedSeconds != null && (
          <span
            className="shrink-0 text-xs tabular-nums text-muted-foreground"
            // The clock only ever moves at the poll's pace, so announcing every
            // tick would make a screen reader read a stopwatch aloud for eight
            // minutes. The step transitions above are the meaningful events.
            aria-hidden="true"
          >
            {formatElapsed(elapsedSeconds)}
          </span>
        )}
      </div>

      <ol className="space-y-1">
        {steps.map((step) => (
          <li
            key={step.id}
            className={cn(
              'flex items-center gap-2 text-xs',
              step.status === 'done' && 'text-muted-foreground',
              step.status === 'running' && 'font-medium text-foreground',
              step.status === 'pending' && 'text-muted-foreground/60',
              step.status === 'failed' && 'text-destructive',
            )}
          >
            <StepIcon status={step.status} />
            <span className="min-w-0 truncate">{step.label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StepIcon({ status }: { status: SoteriaBuildStep['status'] }) {
  if (status === 'done') {
    return <Check className="h-3.5 w-3.5 shrink-0 text-success" />;
  }
  if (status === 'running') {
    return (
      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary motion-reduce:animate-none" />
    );
  }
  if (status === 'failed') {
    return <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />;
  }
  // Pending: an empty ring on the same 3.5 footprint, so nothing shifts
  // sideways as a step changes state.
  return (
    <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-current opacity-50" />
  );
}

/** m:ss once past a minute, plain seconds before that. */
export function formatElapsed(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  if (safe < 60) return `${safe}s`;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
